import {
  QuantumMilestone,
  QuantumApplication,
  BahrainInitiative
} from "@/lib/index";

export const quantumMilestones: QuantumMilestone[] = [
  {
    id: "m1",
    year: 1925,
    title: "The Birth of Matrix Mechanics",
    scientist: "Werner Heisenberg",
    description: "Heisenberg publishes his groundbreaking paper, replacing Bohr's orbits with a system of matrices, marking the formal beginning of quantum mechanics.",
    category: "theory",
    isMajor: true
  },
  {
    id: "m2",
    year: 1926,
    title: "The Wave Equation",
    scientist: "Erwin Schrödinger",
    description: "Schrödinger develops wave mechanics, providing a mathematical framework for the wave-particle duality of matter.",
    category: "theory",
    isMajor: true
  },
  {
    id: "m3",
    year: 1927,
    title: "Uncertainty Principle",
    scientist: "Werner Heisenberg",
    description: "The fundamental limit to the precision with which certain pairs of physical properties of a particle can be known is established.",
    category: "theory",
    isMajor: true
  },
  {
    id: "m4",
    year: 1935,
    title: "The EPR Paradox",
    scientist: "Einstein, Podolsky, & Rosen",
    description: "A thought experiment that challenged the completeness of quantum mechanics, leading to the discovery of quantum entanglement.",
    category: "theory",
    isMajor: false
  },
  {
    id: "m5",
    year: 1947,
    title: "Invention of the Transistor",
    scientist: "Bardeen, Brattain, & Shockley",
    description: "The first practical application of quantum physics in electronics, paving the way for the modern digital age.",
    category: "technology",
    isMajor: true
  },
  {
    id: "m6",
    year: 1960,
    title: "The First Laser",
    scientist: "Theodore Maiman",
    description: "Light amplification by stimulated emission of radiation is demonstrated, utilizing quantum optical principles.",
    category: "technology",
    isMajor: false
  },
  {
    id: "m7",
    year: 1981,
    title: "Quantum Computing Proposal",
    scientist: "Richard Feynman",
    description: "Feynman suggests that computers based on quantum principles could simulate physical systems that classical computers cannot.",
    category: "theory",
    isMajor: true
  },
  {
    id: "m8",
    year: 1994,
    title: "Shor's Algorithm",
    scientist: "Peter Shor",
    description: "A quantum algorithm is discovered that can factor large integers exponentially faster than the best classical algorithms.",
    category: "theory",
    isMajor: false
  },
  {
    id: "m9",
    year: 2019,
    title: "Quantum Supremacy",
    scientist: "Google Quantum AI",
    description: "A quantum processor performs a calculation in minutes that would take a classical supercomputer thousands of years.",
    category: "experiment",
    isMajor: true
  },
  {
    id: "m10",
    year: 2025,
    title: "The Quantum Centennial",
    description: "Global celebration of 100 years of quantum science, marking the transition from theoretical discovery to industrial revolution.",
    category: "technology",
    isMajor: true
  },
  {
    id: "m11",
    year: 2026,
    title: "The Second Quantum Revolution",
    description: "Bahrain and global partners deploy the first commercial-scale quantum-secured communication networks and drug discovery pipelines.",
    category: "technology",
    isMajor: true
  }
];

export const quantumApplications: QuantumApplication[] = [
  {
    id: "a1",
    title: "Quantum Cryptography",
    industry: "Security",
    description: "Uses the principles of quantum mechanics to secure communications through Quantum Key Distribution (QKD), making interception detectable by the laws of physics.",
    impact: "Transformative",
    iconName: "Shield",
    bahrainUseCase: "Implementation of post-quantum cryptographic standards within the National Cyber Security Center (NCSC) frameworks."
  },
  {
    id: "a2",
    title: "Molecular Simulation",
    industry: "Medicine",
    description: "Simulating molecular interactions at the atomic level to discover new drugs and materials with unprecedented speed and accuracy.",
    impact: "Transformative",
    iconName: "FlaskConical",
    bahrainUseCase: "Mumtalakat's partnership with SandboxAQ to accelerate drug discovery for regional health challenges."
  },
  {
    id: "a3",
    title: "Portfolio Optimization",
    industry: "Finance",
    description: "Using quantum algorithms to solve complex optimization problems in financial markets, managing risk and return for massive datasets.",
    impact: "High",
    iconName: "TrendingUp",
    bahrainUseCase: "Exploration of quantum-classical hybrid models within Bahrain FinTech Bay's ecosystem."
  },
  {
    id: "a4",
    title: "Precision Sensing",
    industry: "Communication",
    description: "Quantum sensors that can detect minute changes in gravity, magnetic fields, and time, enabling GPS-free navigation and deep-earth exploration.",
    impact: "High",
    iconName: "Compass",
    bahrainUseCase: "Strategic infrastructure monitoring and geological surveying for national industrial projects."
  },
  {
    id: "a5",
    title: "Catalyst Design",
    industry: "Energy",
    description: "Designing more efficient catalysts for carbon capture and hydrogen production by simulating quantum chemical reactions.",
    impact: "Medium",
    iconName: "Zap",
    bahrainUseCase: "Sustainable energy initiatives aligned with Bahrain's Vision 2030 and net-zero goals."
  }
];

export const bahrainInitiatives: BahrainInitiative[] = [
  {
    id: "b1",
    title: "National Quantum-AI Partnership",
    partner: "SandboxAQ",
    focus: "Healthcare",
    status: "Active",
    description: "A landmark billion-dollar initiative to leverage quantum-inspired AI for drug discovery and life sciences innovation in the Kingdom.",
    establishedYear: 2024
  },
  {
    id: "b2",
    title: "Quantum Security Shield",
    partner: "NCSC Bahrain",
    focus: "Security",
    status: "Active",
    description: "Protecting national digital infrastructure against future quantum-enabled cyber threats using post-quantum cryptography (PQC).",
    establishedYear: 2025
  },
  {
    id: "b3",
    title: "Quantum Computing Lab",
    partner: "University of Bahrain",
    focus: "Education",
    status: "Upcoming",
    description: "Developing the next generation of Bahraini physicists and computer scientists through dedicated quantum research and curriculum.",
    establishedYear: 2026
  },
  {
    id: "b4",
    title: "Strategic Fintech Integration",
    partner: "Mumtalakat",
    focus: "Industry",
    status: "Strategic Partnership",
    description: "Integrating quantum computing capabilities into the sovereign wealth fund's portfolio companies to enhance operational efficiency.",
    establishedYear: 2024
  }
];

export const timelineData = [...quantumMilestones].sort((a, b) => a.year - b.year);
